<template>
  <div class="max-w-4xl mx-auto p-6">
    <div class="bg-white shadow-md rounded-lg p-8">
      <h1 class="text-3xl font-bold mb-8 text-gray-800">Create New Job Search</h1>

      <!-- Progress Steps -->
      <div class="mb-8">
        <div class="flex items-center justify-between">
          <div
            v-for="step in steps"
            :key="step.number"
            class="flex items-center"
            :class="{ 'flex-1': step.number < steps.length }"
          >
            <div class="flex flex-col items-center">
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors"
                :class="currentStep >= step.number ? 'bg-brand-600 text-white' : 'bg-gray-300 text-gray-600'"
              >
                {{ step.number }}
              </div>
              <span
                class="mt-2 text-sm font-medium"
                :class="currentStep >= step.number ? 'text-brand-600' : 'text-gray-500'"
              >
                {{ step.label }}
              </span>
            </div>
            <div
              v-if="step.number < steps.length"
              class="flex-1 h-1 mx-4 transition-colors"
              :class="currentStep > step.number ? 'bg-brand-600' : 'bg-gray-300'"
            ></div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        class="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
      >
        {{ errorMessage }}
      </div>

      <!-- Step 1: Name -->
      <div v-if="currentStep === 1" class="space-y-6">
        <div>
          <label for="searchName" class="block text-sm font-medium text-gray-700 mb-2">
            Search Name
          </label>
          <input
            id="searchName"
            v-model="searchData.title"
            type="text"
            placeholder="e.g., Senior Frontend Developer - Remote"
            class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600"
            @keyup.enter="nextStep"
          />
          <p class="mt-2 text-sm text-gray-500">
            Give your search a descriptive name to easily identify it later
          </p>
        </div>
      </div>

      <!-- Step 2: Location + Work Type -->
      <div v-if="currentStep === 2" class="space-y-6">
        <!-- Location autocomplete -->
        <div class="relative">
          <label for="location" class="block text-sm font-medium text-gray-700 mb-2">
            Location
          </label>
          <input
            id="location"
            v-model="locationQuery"
            type="text"
            placeholder="Start typing a city or region..."
            autocomplete="off"
            class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600"
            @input="onLocationInput"
            @focus="showSuggestions = locationSuggestions.length > 0"
          />
          <!-- Selected location badge -->
          <div v-if="selectedLocation" class="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-brand-100 text-brand-800 rounded-full text-sm">
            {{ selectedLocation.raw_string }}
            <button @click="clearLocation" class="text-blue-500 hover:text-brand-700 font-bold">&times;</button>
          </div>
          <!-- Suggestions dropdown -->
          <ul
            v-if="showSuggestions && locationSuggestions.length > 0"
            class="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-48 overflow-y-auto shadow-lg"
          >
            <li
              v-for="loc in locationSuggestions"
              :key="loc.id"
              class="px-4 py-2 hover:bg-brand-50 cursor-pointer text-sm"
              @mousedown.prevent="selectLocation(loc)"
            >
              {{ loc.raw_string }}
              <span class="text-gray-400 ml-1">{{ [loc.state_province, loc.country].filter(Boolean).join(', ') }}</span>
            </li>
          </ul>
        </div>

        <!-- Work Type -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Work Type</label>
          <div class="flex gap-4">
            <label
              v-for="wt in workTypes"
              :key="wt.value"
              class="flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer transition-colors"
              :class="searchData.work_type === wt.value
                ? 'border-brand-600 bg-brand-50 text-brand-700'
                : 'border-gray-300 hover:bg-gray-50 text-gray-700'"
            >
              <input
                type="radio"
                :value="wt.value"
                v-model="searchData.work_type"
                class="hidden"
              />
              {{ wt.label }}
            </label>
          </div>
        </div>
      </div>

      <!-- Step 3: Skills -->
      <div v-if="currentStep === 3" class="space-y-6">
        <div>
          <label for="overallExperience" class="block text-sm font-medium text-gray-700 mb-2">
            Total Years of Professional Experience <span class="text-red-500">*</span>
          </label>
          <input
            id="overallExperience"
            v-model.number="searchData.overall_experience"
            type="number"
            min="0"
            max="50"
            placeholder="e.g., 5"
            class="w-32 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
          <p class="mt-1 text-sm text-gray-500">
            Your total years of professional experience (used to filter out jobs requiring more experience than you have).
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-4">
            Select Skills for This Search
          </label>

          <div class="mb-4">
            <input
              v-model="skillFilterTerm"
              type="text"
              placeholder="Filter skills..."
              class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-600"
            />
          </div>

          <div class="border border-gray-300 rounded-md p-4 max-h-96 overflow-y-auto">
            <div v-if="filteredMySkills.length === 0" class="text-center text-gray-500 py-8">
              <p>No skills found. Please add skills in the My Skills page first.</p>
              <router-link to="/my-skills" class="text-brand-600 hover:text-brand-700 font-medium mt-2 inline-block">
                Go to My Skills
              </router-link>
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="skill in filteredMySkills"
                :key="skill.skill_id"
                class="flex items-center justify-between p-3 hover:bg-gray-50 rounded-md transition-colors"
              >
                <label class="flex items-center cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    :value="skill.skill_id"
                    v-model="selectedSkillIds"
                    class="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-600"
                  />
                  <span class="ml-3 text-gray-700">{{ skill.skill_name }}</span>
                </label>
                <div v-if="selectedSkillIds.includes(skill.skill_id)" class="ml-4 text-sm text-gray-500">
                  {{ skill.years_experience ?? 0 }} yr{{ (skill.years_experience ?? 0) !== 1 ? 's' : '' }}
                </div>
              </div>
            </div>
          </div>

          <p class="mt-2 text-sm text-gray-500">
            Selected: {{ selectedSkillIds.length }} skill(s)
          </p>
        </div>
      </div>

      <!-- Step 4: Success -->
      <div v-if="currentStep === 4" class="text-center py-8 space-y-4">
        <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 class="text-2xl font-semibold text-gray-800">Job search created!</h2>
        <p class="text-gray-600">Your search is set up and will run within the next 24 hours.</p>
        <p class="text-sm text-gray-500">We'll notify you when matching jobs are found.</p>
      </div>

      <!-- Navigation Buttons -->
      <div class="flex justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          v-if="currentStep > 1 && currentStep < 4"
          @click="previousStep"
          class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
        >
          Previous
        </button>
        <div v-else></div>

        <div class="flex space-x-3">
          <button
            v-if="currentStep < 4"
            @click="cancel"
            class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            v-if="currentStep < 3"
            @click="nextStep"
            :disabled="!canProceed"
            class="px-6 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Next
          </button>
          <button
            v-else-if="currentStep === 3"
            @click="saveSearch"
            :disabled="!canSave || saving"
            class="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {{ saving ? 'Saving...' : 'Save Search' }}
          </button>
          <button
            v-else
            @click="cancel"
            class="px-6 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700"
          >
            View My Searches
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { fetchUserSkills, searchLocations } from '@/services/api';
import api from '@/services/api';
import type { UserSkill, Location, WizardStep, WorkType } from '@/types';

export default {
  name: 'NewJobSearch',
  setup() {
    const router = useRouter();
    const currentStep = ref<number>(1);
    const errorMessage = ref<string>('');
    const saving = ref<boolean>(false);

    const steps: WizardStep[] = [
      { number: 1, label: 'Name' },
      { number: 2, label: 'Location' },
      { number: 3, label: 'Skills' },
      { number: 4, label: 'Done' },
    ];

    const workTypes: WorkType[] = [
      { value: 0, label: 'Onsite' },
      { value: 1, label: 'Remote' },
      { value: 2, label: 'Hybrid' },
    ];

    // Step 1
    const searchData = ref<{ title: string; work_type: number; overall_experience: number | null }>({ title: '', work_type: 0, overall_experience: null });

    // Step 2 - Location
    const locationQuery = ref<string>('');
    const locationSuggestions = ref<Location[]>([]);
    const selectedLocation = ref<Location | null>(null);
    const showSuggestions = ref<boolean>(false);
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const onLocationInput = (): void => {
      selectedLocation.value = null;
      if (debounceTimer) clearTimeout(debounceTimer);
      const q = locationQuery.value.trim();
      if (q.length < 2) {
        locationSuggestions.value = [];
        showSuggestions.value = false;
        return;
      }
      debounceTimer = setTimeout(async () => {
        try {
          const res = await searchLocations(q);
          locationSuggestions.value = res.data.suggestions || [];
          showSuggestions.value = locationSuggestions.value.length > 0;
        } catch {
          locationSuggestions.value = [];
        }
      }, 300);
    };

    const selectLocation = (loc: Location): void => {
      selectedLocation.value = loc;
      locationQuery.value = loc.raw_string;
      showSuggestions.value = false;
    };

    const clearLocation = (): void => {
      selectedLocation.value = null;
      locationQuery.value = '';
    };

    // Step 3 - Skills
    const mySkills = ref<UserSkill[]>([]);
    const skillFilterTerm = ref<string>('');
    const selectedSkillIds = ref<number[]>([]);

    const filteredMySkills = computed<UserSkill[]>(() => {
      const term = skillFilterTerm.value.toLowerCase();
      if (!term) return mySkills.value;
      return mySkills.value.filter((s) =>
        s.skill_name.toLowerCase().includes(term)
      );
    });

    // Navigation
    const canProceed = computed<boolean>(() => {
      if (currentStep.value === 1) return searchData.value.title.trim().length > 0;
      if (currentStep.value === 2) return selectedLocation.value !== null;
      return true;
    });

    const canSave = computed<boolean>(() => {
      return (
        searchData.value.title.trim().length > 0 &&
        selectedLocation.value !== null &&
        selectedSkillIds.value.length > 0 &&
        searchData.value.overall_experience !== null &&
        (searchData.value.overall_experience as number) >= 0
      );
    });

    const nextStep = (): void => {
      if (!canProceed.value) {
        errorMessage.value = 'Please fill in the required field before proceeding.';
        return;
      }
      errorMessage.value = '';
      if (currentStep.value < 3) currentStep.value++;
    };

    const previousStep = (): void => {
      errorMessage.value = '';
      if (currentStep.value > 1) currentStep.value--;
    };

    const cancel = (): void => { router.push('/my-searches'); };

    const saveSearch = async (): Promise<void> => {
      if (!canSave.value) {
        errorMessage.value = 'Please complete all steps before saving.';
        return;
      }
      saving.value = true;
      errorMessage.value = '';

      try {
        const skills = selectedSkillIds.value.map((sid) => ({
          skill_id: sid,
          min_years: mySkills.value.find((s) => s.skill_id === sid)?.years_experience ?? 0,
        }));

        await api.createJobSearch({
          title: searchData.value.title,
          location_id: selectedLocation.value!.id,
          work_type: searchData.value.work_type,
          overall_experience: searchData.value.overall_experience,
          skills,
        });

        currentStep.value = 4;
      } catch (error) {
        console.error('Error saving job search:', error);
        errorMessage.value = 'Failed to save job search. Please try again.';
      } finally {
        saving.value = false;
      }
    };

    onMounted(async () => {
      try {
        const res = await fetchUserSkills();
        mySkills.value = res.data;
      } catch (error) {
        console.error('Error fetching user skills:', error);
        errorMessage.value = 'Failed to load your skills.';
      }
    });

    return {
      currentStep,
      steps,
      workTypes,
      searchData,
      errorMessage,
      saving,
      locationQuery,
      locationSuggestions,
      selectedLocation,
      showSuggestions,
      onLocationInput,
      selectLocation,
      clearLocation,
      mySkills,
      skillFilterTerm,
      selectedSkillIds,
      filteredMySkills,
      canProceed,
      canSave,
      nextStep,
      previousStep,
      cancel,
      saveSearch,
    };
  },
};
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
